import {ContentKind, TagGroup, TagID, TagRelationParams} from '/@src/models/api'
import useApi from "/@src/composable/useApi";
import {Content, TagData} from "/@src/models/content";
import {updateTagRelations} from "/@src/api/tagRepo";
import {ref} from "vue";
import {Detail, Tag} from "/@src/models/graphApi";
import {FormData} from "/@src/models/form";
import {RelationKind, TagRelation} from "/@src/__generated__/generated-gql-types";

export interface ContentTagRelationConfig{
  /**
   * 是否精选
   */
  featured?:boolean
  /**
   * 最新动态
   */
  isLatest?:boolean
  kind?:ContentKind
  /**
   * 是否仅显示在终端
   */
  isMatrixTerminalOnly?:boolean
  /**
   * 是否显示在web首页信息流
   */
  isWebHome?:boolean,
  /**
   * 是否原创
   */
  originalContent?:boolean
}

export default function useTagRelations() {
  const api = useApi()
  const tagRelationLoading = ref(false)
  /**
   * 添加至标签
   * @param tagId
   * @param group
   * @param list
   */
  const addToTagGroup = async (tagId: number,group:TagGroup,list:Array<Content>) => {
    if(list.length>0){
      for (let index=0;index<list.length;index++){
        let shouldUpdate = false
        const content = list[index]
        let relations = content.tag_relations
        //检查是否已经添加过
        if(relations&&relations.length>0){
          let isAdded = false
          for (let innerIndex=0;innerIndex<relations.length;innerIndex++){
            const relation = relations[innerIndex]
            if(relation.id === tagId){
              if(group===TagGroup.ALL||relation.group === group){
                isAdded = true
                //历史遗留问题，功能性group使用非0值
                if(group === 0){
                  group = TagGroup.ALL
                }
                break
              }
            }
          }
          //未添加过该分组
          if(!isAdded){
            shouldUpdate = true
            relations.push(makeRelation(tagId,group))
          }
        }else {
          shouldUpdate = true
          relations = []
          relations.push(makeRelation(tagId,group))
        }
        console.log('shouldUpdate:'+shouldUpdate)
        // 未添加过该tag时
        if(shouldUpdate){
          const newRelation = relations.map(({ k, ...relation }) => relation)
          await updateTagRelations(api,content.id!!,content.kind,newRelation)
        }
      }
    }
  }
  const makeRelation = (id:number,group:TagGroup):TagRelation => {
    const newRelation:TagRelation = {
      id:id
    }
    newRelation.group = group
    return newRelation
  }
  /**
   * 给指定的relation添加关系
   * @param tagId
   * @param group TagGroup.ALL表示全部组别
   * @param relations
   */
  const addTagRelation = (tagId: number,group:TagGroup,relations?:TagRelation[]):TagRelation[] => {
     if(!relations){
       relations = new Array()
     }
     let isFind = false
     for(const r of relations){
       if(tagId === r.id ){
         if(group === r.group || group === TagGroup.ALL){
           isFind = true
           break
         }else if(r.group === TagGroup.ALL||r.group === TagGroup.NONE){
           // 如果已经加过全部分类，则将该全部分类替换为特定的组别（全部和某个特定的组别互斥）
           r.group = group
           isFind = true
           break
         }
       }
     }
     if(!isFind){
       relations.push({
         id:tagId,
         group:group,
         k:RelationKind.Tag
       })
     }
    return relations
  }

  /**
   * 删除指定标签关系
   * @param tagId
   * @param group TagGroup.ALL 则删除所有该tag下的关系
   * @param relations
   */
  const removeTagRelation = (tagId: number,group:TagGroup,relations?:TagRelation[]):TagRelation[] => {
    if(!relations){
      return []
    }
    const newRelation = new Array<TagRelation>()
    let isGroupAllChanged = false
    for(const r of relations){
      if(tagId === r.id){
        if(group===TagGroup.ALL){
          //删除全部则表示全删除
          continue
        }else if(group === r.group){
          //当删除某个具体分组时，将其置于默认组别
          if(!isGroupAllChanged){
            r.group = TagGroup.NONE
            newRelation.push(r)
            isGroupAllChanged = true
          }
        }else if(r.group === TagGroup.ALL){
          // 全部组别只会出现一次
          if(!isGroupAllChanged){
            newRelation.push(r)
            isGroupAllChanged = true
          }
        }
      }else {
        newRelation.push(r)
      }
    }
    return newRelation
  }
  /**
   * 移除tag关系
   * @param tagId
   * @param group
   * @param relations
   */
  const removeRelation = async (tagId: number,group:TagGroup,relationsParam:TagRelationParams) => {
    if(relationsParam.tagRelations&&relationsParam.tagRelations.length>0){
      const newRelation:TagRelation[] = []
      for (let index=0;index<relationsParam.tagRelations.length;index++){
        const relation = relationsParam.tagRelations[index]
        if(relation.id === tagId){
          // 如果是删除全部分组内，则其他分组一并删除
          if(group === TagGroup.ALL){
            continue
          }
          if(relation.group === group){
            continue
          }
          newRelation.push(relation)
        }else {
          newRelation.push(relation)
        }
      }
      await updateTagRelations(api,relationsParam.contentId,relationsParam.kind,newRelation)
    }
  }
  /**
   * 该内容是否属于最新内容的组别
   * @param relations
   */
  const isContentLatest = (relations?:TagRelation[]):boolean => {
    if(!relations){
      return false
    }
    for(const relation of relations){
      if(relation.id === TagID.MATRIX && relation.group === TagGroup.LATEST){
        return true
      }
    }
    return false
  }
  /**
   * 给content设置tags和tag relation
   * @param content
   * @param tags
   * @param tag_relations
   */
  const formatTagRelations = (content: FormData<any>,tags:Tag[],tag_relations:TagRelation[]) => {
    // 标签
    if(tags){
      content.tags = []
      tags.forEach(it=>{
        content.tags!!.push({
          id:it!!.id!!,
          name:it!!.name!!,
          cover_url:it!!.cover_url!!,
          description:it!!.description
        })
      })
    }
    // 标签关系
    if(tag_relations){
      content.tagRelations = []
      tag_relations.forEach(it=>{
        if(it!.id === TagID.MATRIX && it.group === TagGroup.FEATURED_FLOW){
          // 0为首页的特殊id
          content.isFeatured = true
        }
        content.tagRelations!!.push({
          id:it!!.id,
          group:it!!.group
        })
      })
    }
  }
  const addNewTags = (content:Content,tags:Tag[])=>{
    if(tags&&tags.length>0){
      content.tag_relations = []
      tags.forEach(it=>{
        content.tag_relations!!.push({
          id:it!!.id!!
        })
      })
    }
  }
  /**
   * 根据tag设置tag relation
   * @param form
   * @param kind
   */
  const setupTagRelations = (form: FormData<Detail>,kind:ContentKind)=>{
    form.tagRelations = setupContentTagRelations(form.tags!, form.tagRelations!, {
      featured: form.isFeatured,
      // 默认显示在最近更新
      isLatest: form.isLatest||form.isLatest === undefined,
      // 默认显示在首页
      isWebHome: form.isHome||form.isHome === undefined,
      originalContent: form.isOriginal,
      kind: kind
    })
  }
  const setupContentTagRelations = (tags: Tag[]|undefined,tagRelations:TagRelation[]|undefined,config:ContentTagRelationConfig):TagRelation[]=>{
    const newRelations:TagRelation[] = []
    if(tagRelations){
      if(tags){
        //根据tag顺序设置tag relation
        tags.forEach(tag => {
          tagRelations.forEach(relation => {
            if(tag.id == relation.id){
              if(relation.group === 0){
                relation.group = TagGroup.ALL
              }
              newRelations.push(relation)
            }
          })
        })
      }else {
        tagRelations.forEach(value => {
          if(value.id != TagID.MATRIX){
            newRelations.push(value)
          }
        })
      }
    }
    if(config.isLatest){
      // 加入最新更新流
      newRelations.push({
        id:TagID.MATRIX,
        group:TagGroup.LATEST
      })
    }
    if(config.featured){
      newRelations.push({
        id:TagID.MATRIX,
        group:TagGroup.FEATURED_FLOW
      })
    }
    // 总是加入终端
    newRelations.push({
      id:TagID.MATRIX,
      group:TagGroup.MATRIX_TERMINAL
    })
    if(config.isWebHome  !== false){
      newRelations.push({
        id:TagID.MATRIX,
        group:TagGroup.WEBSITE_HOME
      })
    }
    if(config.originalContent){
      newRelations.push({
        id:TagID.MATRIX,
        group:TagGroup.ORIGINAL_CONTENT
      })
    }
    let kindGroup = -1
    switch (config.kind) {
      case ContentKind.ARTICLE:
        kindGroup = TagGroup.RESEARCH_ARTICLES
        break
      case ContentKind.VIDEO:
        kindGroup = TagGroup.RESEARCH_VIDEOS
        break
      case ContentKind.FAQ:
        kindGroup = TagGroup.FAQ
        break
      case ContentKind.ROADMAP:
        kindGroup = TagGroup.ROADMAP
        break
      case ContentKind.ROADSHOW:
        kindGroup = TagGroup.ROADSHOW
        break
      case ContentKind.REPORT:
        kindGroup = TagGroup.REPORT
        break
      case ContentKind.LINK:
        kindGroup = TagGroup.LINK
        break
      case ContentKind.CALENDAR:
        kindGroup = TagGroup.FLASH
        break
    }
    // terminal组以外
    const pushKindGroup = kindGroup !== -1 && !config.isMatrixTerminalOnly
    if(pushKindGroup){
      newRelations.push({
        id:TagID.MATRIX,
        group:kindGroup
      })
    }
    return newRelations
  }
  const updateContentRelation = (id:number,kind:ContentKind,relations:TagRelation[]):Promise<Tag> => {
    return  updateTagRelations(api,id,kind,relations)
  }
  /**
   * 根据relation更新tag
   * @param relations
   * @param tags
   */
  const updateTagsWithRelations = (relations?:TagRelation[],tags?:Tag[]):Array<Tag> => {
    const newTags = new Array<Tag>()
    if(!relations||relations.length===0||!tags){
      return newTags
    }
    const tagsMap = new Map<number,Tag>()
    relations.forEach((value, index) => {
      if(value.id !== TagID.MATRIX){
        for(const tag of tags){
          if(tag.id === value.id){
            tagsMap.set(tag.id!,tag)
            // 顺便检测是否是典藏库
            if(value.group === TagGroup.TREASURY){
              (tag as TagData).treasury = true
            }
            break
          }
        }
      }
    })
    return Array.from(tagsMap.values())
  }
  /**
   * 是否有指定的group
   *
   * @param relations
   * @param tagId
   * @param group
   */
  const has = (relations:TagRelation[],tagId:number, group:TagGroup) => {
    return relations.findIndex((value)=>{
      return value.id === tagId &&  value.group === group
    })>=0
  }

  return {
    has,
    addToTagGroup,
    removeRelation,
    isContentLatest,
    addNewTags,
    setupContentTagRelations,
    formatTagRelations,
    setupTagRelations,
    addTagRelation,
    removeTagRelation,
    updateContentRelation,
    updateTagsWithRelations
  }
}
